import { ColumnFiltersState } from '@tanstack/react-table';
import { germanStartOfDayInstant } from './date-time.formatter';
import { BmweColumnMeta, TextFilterValue } from '../types';
import { FilterMode } from '../components';

/**
 * Sentinel value used in select-filter arrays to represent the "(leer)" /
 * "empty" option. When present in a filter's value array, the mapper emits a
 * null-check clause (FK is null for nested relation fields, field is null for
 * simple fields). Custom mappers — e.g. PARTY_ROLE / collection filters in
 * dossiersList — must opt in by detecting this sentinel themselves and
 * choosing the right `none: {}` shape.
 */
export const EMPTY_FILTER_VALUE = '__POEM_EMPTY__';

export const isTextFilterValue = (value: unknown): value is TextFilterValue =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as TextFilterValue).values) &&
  Array.isArray((value as TextFilterValue).texts);

/** Reads a value/text filter, including the plain text and value lists of older saved views. */
export const toTextFilterValue = (value: unknown): TextFilterValue => {
  if (isTextFilterValue(value)) return value;
  if (Array.isArray(value)) return { values: value as string[], texts: [] };
  if (typeof value === 'string' && value.trim()) {
    return { values: [], texts: [value.trim()] };
  }
  return { values: [], texts: [] };
};

export type FilterKind = 'select' | 'text' | 'other';

export const filterKindOf = (meta?: BmweColumnMeta): FilterKind => {
  const type = meta?.filterType ?? meta?.dataType;
  if (type === 'select') return 'select';
  return !type || type === 'string' ? 'text' : 'other';
};

const isBlankFilterValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (typeof value === 'object' &&
    !(value instanceof Date) &&
    Object.values(value).every(
      (v) => v === undefined || v === null || v === '',
    ));

export const normalizeFilterValue = (
  meta: BmweColumnMeta | undefined,
  draft: unknown,
): unknown => {
  const kind = filterKindOf(meta);
  if (kind === 'other') return isBlankFilterValue(draft) ? undefined : draft;
  const { values, texts } = toTextFilterValue(draft);
  if (kind === 'select') return values.length > 0 ? values : undefined;
  return values.length + texts.length > 0 ? { values, texts } : undefined;
};

// The audit timestamps are `DateTime`; every other date column is `LocalDate`,
// which takes the picker's `YYYY-MM-DD` as is.
const DATE_TIME_FIELDS = new Set(['created', 'modified']);

export const mapToGraphQlFilterInput = <T extends Record<string, unknown>>(
  columnFilters: ColumnFiltersState,
  mode: FilterMode = 'and',
): T => {
  if (!columnFilters?.length) {
    return {} as T;
  }

  if (columnFilters.length === 1) {
    return (createFilterCondition(columnFilters[0]) ?? {}) as T;
  }

  const conditions = columnFilters.map(createFilterCondition).filter(Boolean);

  return conditions.length
    ? ({ [mode]: conditions } as unknown as T)
    : ({} as T);
};

// Create a single filter condition
const createFilterCondition = (filter: {
  id: string;
  value: unknown;
}): Record<string, unknown> | null => {
  const { id, value } = filter;

  // Skip empty values
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    value === 'all' ||
    (Array.isArray(value) && value.length === 0) ||
    isEmptyObject(value)
  ) {
    return null;
  }

  // Picked values match exactly, typed texts by "contains"; any of them may match.
  if (isTextFilterValue(value)) {
    const clauses = [
      value.values.length
        ? createFilterCondition({ id, value: value.values })
        : null,
      ...value.texts.map((text) => createFilterCondition({ id, value: text })),
    ].filter((clause): clause is Record<string, unknown> => clause !== null);
    if (clauses.length === 0) return null;
    return clauses.length === 1 ? clauses[0] : { or: clauses };
  }

  // "(leer)" sentinel handling for multi-select arrays. For nested ids like
  // `priority.name` we test the FK (`priorityId: { eq: null }`) since HotChocolate
  // won't accept `priority: { eq: null }` on a relation filter input. Simple ids
  // are string fields, see `buildEmptyClause`.
  if (Array.isArray(value) && value.includes(EMPTY_FILTER_VALUE)) {
    const others = value.filter((v) => v !== EMPTY_FILTER_VALUE);
    const emptyClause = buildEmptyClause(id);
    if (others.length === 0) {
      return emptyClause;
    }
    const nonEmptyClause = {} as Record<string, unknown>;
    if (id.includes('.')) {
      applyNestedField(nonEmptyClause, id, others);
    } else {
      nonEmptyClause[id] = determineFilterOperation(others);
    }
    return { or: [emptyClause, nonEmptyClause] };
  }

  const operation =
    DATE_TIME_FIELDS.has(id) && isDateRange(value)
      ? toDateTimeRange(value)
      : determineFilterOperation(value);
  if (Object.keys(operation).length === 0) {
    return null;
  }

  const condition = {} as Record<string, unknown>;

  if (id.includes('.')) {
    applyNestedField(condition, id, value);
  } else {
    condition[id] = operation;
  }

  return condition;
};

const buildEmptyClause = (id: string): Record<string, unknown> => {
  if (id.includes('.')) {
    const [root] = id.split('.');
    return { [`${root}Id`]: { eq: null } };
  }
  // HotChocolate rejects `eq: null` on non-nullable strings; every string
  // starts with '', so this matches exactly the null values and is always valid.
  return { [id]: { nstartsWith: '' } };
};

// Apply nested field structure
const applyNestedField = (
  filter: Record<string, unknown>,
  id: string,
  value: unknown,
): void => {
  const fieldParts = id.split('.');
  const [rootField, ...parts] = fieldParts;
  const lastField = parts.pop() as string;

  // Create root if needed
  filter[rootField] = filter[rootField] || {};

  // Navigate to right level
  const targetObj = parts.reduce(
    (obj, part) => {
      obj[part] = obj[part] || {};
      return obj[part] as Record<string, unknown>;
    },
    filter[rootField] as Record<string, unknown>,
  );

  // Set final value
  targetObj[lastField] = determineFilterOperation(value);
};

// Check if value is a plain empty object
const isEmptyObject = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date) &&
  Object.keys(value).length === 0;

// Check if value is a date range object { from?: string; to?: string }
const isDateRange = (value: unknown): value is { from?: string; to?: string } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date) &&
  ('from' in value || 'to' in value);

// Whole German days: from midnight of `from` up to, not including, the
// midnight after `to`.
const toDateTimeRange = (value: {
  from?: string;
  to?: string;
}): Record<string, unknown> => {
  const range: Record<string, unknown> = {};
  if (value.from) range.gte = germanStartOfDayInstant(value.from).toISOString();
  if (value.to) range.lt = germanStartOfDayInstant(value.to, 1).toISOString();
  return range;
};

// Check if value is a number range object { min?, max? }
const isNumberRange = (
  value: unknown,
): value is { min?: number; max?: number } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value instanceof Date) &&
  ('min' in value || 'max' in value);

// Determine the filter operation based on value type
const determineFilterOperation = (value: unknown): Record<string, unknown> => {
  if (value instanceof Date) {
    return { gte: value.toISOString() };
  }

  if (isNumberRange(value)) {
    const range: Record<string, unknown> = {};
    if (value.min != null) range.gte = value.min;
    if (value.max != null) range.lte = value.max;
    return Object.keys(range).length > 0 ? range : {};
  }

  if (isDateRange(value)) {
    const range: Record<string, unknown> = {};
    if (value.from) range.gte = value.from;
    if (value.to) range.lte = value.to;
    return Object.keys(range).length > 0 ? range : {};
  }

  if (Array.isArray(value) && value.length > 0) {
    return { in: value };
  }

  switch (typeof value) {
    case 'string':
      return { contains: value };
    default:
      return { eq: value };
  }
};
